/* eslint-disable react-refresh/only-export-components -- fabriques de panneaux de détail (fonctions renvoyant un Detail), aucun composant exporté : pas d’enjeu de Fast Refresh */
import type { ActivityKey, DashboardPayload, SplitCategory, Weather } from '@/api/dashboard-live';
import type { Detail } from './context';
import type { ChartPalette } from './theme';
import { AlertList, DataTable, OkMark, Row } from './ui';
import { ago, bytes, dateTime, dec, duration, ms, num, pct, time } from './format';
import { DailyChart, HourlyBars, TimeChart, Legend } from './charts';

export const ROLE_LABEL = { student: 'Élèves', parent: 'Parents', teacher: 'Enseignants', staff: 'Personnel', thirdParty: 'Tiers autorisés' } as const;

export const ACTIVITY_LABEL: Record<ActivityKey, string> = {
  attendanceCalls: 'Appels',
  grades: 'Notes',
  evaluations: 'Évaluations',
  textbookEntries: 'Cahier de textes',
  homeworks: 'Devoirs',
  messages: 'Messages',
  announcements: 'Annonces',
  mails: 'E-mails',
  mailsFailed: 'E-mails en échec',
  documents: 'Documents GED',
  formSubmissions: 'Formulaires reçus',
  invoices: 'Factures',
};

export const SPLIT_LABEL: Record<SplitCategory, string> = {
  v1Only: 'V1 seule',
  both: 'V1 et V2',
  v2Only: 'V2 seule',
  none: 'Aucune connexion',
};

export function splitColors(p: ChartPalette): Record<SplitCategory, string> {
  return { v1Only: p.v1, both: p.third, v2Only: p.v2, none: p.muted };
}

// ---------------------------------------------------------------- météo

const WEATHER_EXPLAIN: Record<Weather['key'], string> = {
  v1: 'Sonde sur la page de connexion V1, trafic réel (logs d\'ingress : erreurs 5xx et temps de réponse), tâches planifiées V1, base MariaDB.',
  v2: 'Sondes sur l\'API (/api/v1/auth/tenants doit répondre 401) et le front, trafic réel de l\'API, files LavinMQ (stock, messages en échec), base PostgreSQL, e-mails en échec.',
  data: 'Sonde applicative sur la nomenclature (vraie requête PHP + base), erreurs 5xx des appels internes (logs nginx des pods data).',
  infra: 'Nœuds AKS (CPU, mémoire, disque système, état Ready), pods en erreur ou qui redémarrent, répliques manquantes, capacité en pods du pool applicatif, tâches planifiées communes.',
  ops: 'Exploitation ADM : sauvegardes des dernières 26 h, cron ef:metrics:get, année fiscale courante, rattrapage automatique, lots de migration en échec, collecte du tableau lui-même.',
};

export function weatherDetail(w: Weather, payload: DashboardPayload): Detail {
  const cats: Record<Weather['key'], string[]> = { v1: ['v1'], v2: ['v2'], data: ['data'], infra: ['infra'], ops: ['ops', 'adm', 'migration'] };
  const alerts = payload.alerts.filter((a) => cats[w.key].includes(a.category));
  return {
    title: `Météo ${w.label}`,
    subtitle: w.stateLabel,
    explain: (
      <>
        <p>{WEATHER_EXPLAIN[w.key]}</p>
        <p>
          La météo découle des alertes ouvertes : <b>soleil</b> sans alerte, <b>éclaircies</b> avec 1 alerte « attention », <b>pluie</b> à partir de 2, <b>orage</b> dès
          une alerte critique. Les alertes « info » ne changent pas la météo.
        </p>
      </>
    ),
    body: <AlertList alerts={alerts} empty="Aucune alerte sur cette plateforme" />,
  };
}

// ---------------------------------------------------------------- usage

export function v1Detail(payload: DashboardPayload, p: ChartPalette): Detail {
  const d = payload.sections.v1?.data;
  return {
    title: 'Usage V1 aujourd\'hui',
    explain: (
      <>
        <p>
          <b>Actifs</b> = utilisateurs dont la date de dernière activité (<code>lastlogin_date</code> des élèves, parents — père et mère —, enseignants et personnel)
          est d'aujourd'hui. L'appli mobile V1 la met à jour à chaque appel, le web à chaque connexion : c'est la mesure complète. « En ligne » = actif dans les
          15 dernières minutes.
        </p>
        <p>
          <b>Connexions</b> = table <code>loginlog</code> (logins explicites). Elle ignore l'appli mobile une fois connectée (jeton de 10 à 180 jours) et compte
          environ un tiers d'utilisateurs en moins. Jusqu'au 30/09/2026, l'historique des actifs V1 repose sur elle (seule source datée) : il est sous-estimé.
        </p>
      </>
    ),
    body: d && (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6">
          <Row label="Utilisateurs actifs" value={num(d.activeUsersToday)} />
          <Row label="Connexions (loginlog)" value={num(d.loginsToday)} hint={d.loginUsersToday !== undefined ? `${num(d.loginUsersToday)} utilisateurs` : undefined} />
          {(['student', 'parent', 'teacher', 'staff'] as const).map((r) => (
            <Row key={r} label={ROLE_LABEL[r]} value={num(d.activeByRole[r])} />
          ))}
          <Row label="Écoles actives" value={num(d.activeSchoolsToday)} hint={`/ ${num(d.schoolsProduction)}`} />
          <Row label="En ligne (15 min)" value={num(d.online15m)} />
        </div>
        <div>
          <h4 className="mb-1 text-xs font-medium uppercase text-slate-400">Connexions par heure</h4>
          <HourlyBars values={d.hourly} color={p.v1} />
        </div>
        <DataTable
          rows={d.bySchool}
          columns={[
            { key: 'code', label: 'École', render: (r) => <span title={r.name}>{r.code}</span> },
            { key: 'u', label: 'Utilisateurs', align: 'right', render: (r) => num(r.users) },
            { key: 's', label: 'Élèves', align: 'right', render: (r) => num(r.byRole.student) },
            { key: 'p', label: 'Parents', align: 'right', render: (r) => num(r.byRole.parent) },
            { key: 't', label: 'Ens.', align: 'right', render: (r) => num(r.byRole.teacher) },
            { key: 'st', label: 'Pers.', align: 'right', render: (r) => num(r.byRole.staff) },
            { key: 'l', label: 'Dernière', align: 'right', render: (r) => time(r.lastLoginAt) },
          ]}
        />
      </div>
    ),
  };
}

export function v2Detail(payload: DashboardPayload, p: ChartPalette): Detail {
  const d = payload.sections.v2?.data;
  const t = payload.sections.v2Tenants?.data;
  return {
    title: 'Usage V2 aujourd\'hui',
    explain: (
      <>
        <p>
          <b>Utilisateurs actifs</b> = comptes distincts ayant obtenu un jeton depuis minuit (<code>refresh_tokens</code>, une ligne par rotation) : un utilisateur resté
          connecté compte, contrairement au seul login par mot de passe (« connexions »). Les comptes internes École Futée (support, ADM, connecteurs) sont
          exclus.
        </p>
        <p>
          <b>Par rôle</b> : <code>user_role.last_connection_at</code> de chaque base tenant, balayées toutes les 15 min (personnes de l'année scolaire en cours). Les
          administrateurs internes École Futée n'y figurent pas.
        </p>
      </>
    ),
    body: d && (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6">
          <Row label="Utilisateurs actifs" value={num(d.activeUsersToday)} />
          <Row label="Connexions (mot de passe)" value={num(d.loginsToday)} />
          <Row label="En ligne (15 min)" value={num(d.online15m)} />
          <Row label="Comptes créés aujourd'hui" value={num(d.accountsCreatedToday)} />
          <Row label="Écoles ouvertes aujourd'hui" value={num(d.schoolsEnteredToday)} />
          <Row label="Comptes au total" value={num(d.accountsTotal)} />
          {t &&
            (['student', 'parent', 'teacher', 'staff', 'thirdParty'] as const).map((r) => (
              <Row key={r} label={`${ROLE_LABEL[r]} actifs`} value={num(t.activeToday[r])} hint={`/ ${num(t.totals[r])}`} />
            ))}
        </div>
        <div>
          <h4 className="mb-1 text-xs font-medium uppercase text-slate-400">Utilisateurs actifs par heure</h4>
          <HourlyBars values={d.hourly} color={p.v2} />
        </div>
        {t && (
          <DataTable
            rows={t.perTenant}
            columns={[
              { key: 'code', label: 'École', render: (r) => <span title={r.name}>{r.code}</span> },
              { key: 'a', label: 'Actifs', align: 'right', render: (r) => num(r.activeTotal) },
              { key: 's', label: 'Élèves', align: 'right', render: (r) => `${num(r.active.student)} / ${num(r.totals.student)}` },
              { key: 'p', label: 'Parents', align: 'right', render: (r) => `${num(r.active.parent)} / ${num(r.totals.parent)}` },
              { key: 't', label: 'Ens.', align: 'right', render: (r) => `${num(r.active.teacher)} / ${num(r.totals.teacher)}` },
              { key: 'n', label: 'Nouveaux', align: 'right', render: (r) => num(r.new.student + r.new.parent) },
              { key: 'l', label: 'Dernière', align: 'right', render: (r) => time(r.lastConnectionAt) },
            ]}
          />
        )}
      </div>
    ),
  };
}

export function newUsersDetail(payload: DashboardPayload): Detail {
  const t = payload.sections.v2Tenants?.data;
  const rows = (t?.perTenant ?? []).filter((r) => r.new.student + r.new.parent + r.new.teacher + r.new.staff > 0).sort((a, b) => b.new.student + b.new.parent - (a.new.student + a.new.parent));
  return {
    title: 'Nouveaux utilisateurs V2 aujourd\'hui',
    explain: <p>Personnes (<code>school_user</code>) créées depuis minuit dans chaque base tenant, par rôle sur l'année scolaire en cours. Un import massif apparaît ici.</p>,
    body: t && (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6">
          {(['student', 'parent', 'teacher', 'staff', 'thirdParty'] as const).map((r) => (
            <Row key={r} label={ROLE_LABEL[r]} value={num(t.newToday[r])} />
          ))}
        </div>
        <DataTable
          rows={rows}
          empty="Aucune création aujourd'hui"
          columns={[
            { key: 'code', label: 'École', render: (r) => <span title={r.name}>{r.code}</span> },
            { key: 's', label: 'Élèves', align: 'right', render: (r) => num(r.new.student) },
            { key: 'p', label: 'Parents', align: 'right', render: (r) => num(r.new.parent) },
            { key: 't', label: 'Ens.', align: 'right', render: (r) => num(r.new.teacher) },
            { key: 'st', label: 'Pers.', align: 'right', render: (r) => num(r.new.staff) },
          ]}
        />
      </div>
    ),
  };
}

export function activityDetail(payload: DashboardPayload, key?: ActivityKey): Detail {
  const t = payload.sections.v2Tenants?.data;
  const k = key ?? 'attendanceCalls';
  const rows = (t?.perTenant ?? []).filter((r) => (r.activity?.[k] ?? 0) > 0).sort((a, b) => (b.activity[k] ?? 0) - (a.activity[k] ?? 0));
  return {
    title: key ? `${ACTIVITY_LABEL[k]} créés aujourd'hui (V2)` : 'Activité V2 aujourd\'hui',
    explain: <p>Lignes créées depuis minuit dans les tables métier de chaque base tenant (appels, notes, cahier de textes, messages, e-mails, GED, formulaires, factures).</p>,
    body: t && (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6">
          {(Object.keys(ACTIVITY_LABEL) as ActivityKey[]).map((a) => (
            <Row key={a} label={ACTIVITY_LABEL[a]} value={num(t.activity[a])} />
          ))}
        </div>
        <h4 className="text-xs font-medium uppercase text-slate-400">Par école — {ACTIVITY_LABEL[k]}</h4>
        <DataTable
          rows={rows}
          empty="Aucune ligne aujourd'hui"
          columns={[
            { key: 'code', label: 'École', render: (r) => <span title={r.name}>{r.code}</span> },
            { key: 'v', label: ACTIVITY_LABEL[k], align: 'right', render: (r) => num(r.activity[k]) },
            { key: 'a', label: 'Appels', align: 'right', render: (r) => num(r.activity.attendanceCalls) },
            { key: 'g', label: 'Notes', align: 'right', render: (r) => num(r.activity.grades) },
            { key: 'm', label: 'Messages', align: 'right', render: (r) => num(r.activity.messages) },
          ]}
        />
      </div>
    ),
  };
}

export function splitDetail(payload: DashboardPayload, p: ChartPalette, category?: SplitCategory): Detail {
  const d = payload.sections.platform?.data;
  const rows = (d?.split.schools ?? []).filter((s) => !category || s.category === category);
  const colors = splitColors(p);
  return {
    title: category ? `Écoles « ${SPLIT_LABEL[category]} » aujourd'hui` : 'Où se connectent les écoles aujourd\'hui',
    explain: (
      <p>
        Pour chaque école en production (V1 ou V2) : actifs du jour en V1 (<code>lastlogin_date</code>, web et appli mobile) et en V2 (actifs du balayage tenant, à
        défaut entrées dans l'école). « V1 seule » = aucune activité V2 aujourd'hui : ce sont les écoles à accompagner.
      </p>
    ),
    body: (
      <DataTable
        rows={rows}
        columns={[
          { key: 'code', label: 'École', render: (r) => <span title={r.name}>{r.code}</span> },
          {
            key: 'c',
            label: 'Usage',
            render: (r) => (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: colors[r.category] }} aria-hidden />
                {SPLIT_LABEL[r.category]}
              </span>
            ),
          },
          { key: 'v1', label: 'Actifs V1', align: 'right', render: (r) => num(r.v1Users) },
          { key: 'v2', label: 'Actifs V2', align: 'right', render: (r) => num(r.v2Users) },
          { key: 's', label: 'Part V2', align: 'right', render: (r) => pct(r.v2SharePct, 0) },
          { key: 'dep', label: 'Déploiements', render: (r) => <span className="text-xs text-slate-400">V1 {r.v1 ?? '–'} · V2 {r.v2 ?? '–'}</span> },
        ]}
      />
    ),
  };
}

export function migrationRateDetail(payload: DashboardPayload, p: ChartPalette): Detail {
  const d = payload.sections.platform?.data;
  return {
    title: 'Taux de migration',
    explain: (
      <>
        <p>
          <b>Part d'usage V2</b> = actifs V2 / (actifs V1 + actifs V2) du jour. C'est l'indicateur de fond de la bascule : il mesure où travaillent réellement les
          utilisateurs.
        </p>
        <p>
          <b>Écoles basculées</b> = parmi les écoles actives aujourd'hui, celles dont au moins 80 % des actifs sont en V2 (quelques parents restés sur l'ancienne
          appli ne comptent plus). <b>Écoles 100 % V2</b> = aucune connexion V1, critère plus strict. <b>Parc équipé V2</b> = écoles ayant un déploiement V2 en
          production (tenant créé), qu'elles l'utilisent ou non.
        </p>
      </>
    ),
    body: d && (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6">
          <Row label="Part d'usage V2" value={pct(d.rates.usageV2Pct)} />
          <Row label="Écoles basculées (≥ 80 % V2)" value={pct(d.rates.schoolsMostlyV2Pct)} hint={`${num(d.rates.schoolsMostlyV2)} / ${num(d.rates.schoolsActive)}`} />
          <Row label="Écoles 100 % V2 (actives)" value={pct(d.rates.schoolsV2OnlyPct)} />
          <Row label="Parc équipé V2" value={pct(d.rates.fleetV2Pct)} hint={`${num(d.fleet.v2Production)} / ${num(d.fleet.total)}`} />
          <Row label="Écoles V1 sans V2" value={num(d.fleet.v1OnlyProduction)} />
        </div>
        <h4 className="text-xs font-medium uppercase text-slate-400">Part d'usage V2 (fin de journée)</h4>
        <DailyChart defs={[{ metric: 'migration.usage_v2_pct', label: 'Part d\'usage V2', color: p.v2 }]} format={(n) => `${dec(n, 0)} %`} domainMax={100} height={180} />
      </div>
    ),
  };
}

export function migrationProjectsDetail(payload: DashboardPayload): Detail {
  const m = payload.sections.platform?.data?.migration;
  return {
    title: 'Projets de migration V1 → V2',
    explain: <p>Projets de l'outil de migration de l'ADM (tables mig_*) : statut, enregistrements traités, lots (étapes) par statut, derniers projets actifs.</p>,
    body: m && (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6">
          {Object.entries(m.projects).map(([k, v]) => (
            <Row key={k} label={`Projets ${k}`} value={num(v)} />
          ))}
          <Row label="Enregistrements traités" value={num(m.records.processed)} hint={`/ ${num(m.records.total)}`} />
          <Row label="Enregistrements en échec" value={num(m.records.failed)} />
          {Object.entries(m.steps).map(([k, v]) => (
            <Row key={k} label={`Lots ${k}`} value={num(v)} />
          ))}
        </div>
        <DataTable
          rows={m.recent}
          columns={[
            { key: 'code', label: 'École', render: (r) => <span title={r.name}>{r.code}</span> },
            { key: 's', label: 'Statut', render: (r) => r.status },
            { key: 'p', label: 'Traités', align: 'right', render: (r) => `${num(r.processed)} / ${num(r.total)}` },
            { key: 'f', label: 'Échecs', align: 'right', render: (r) => num(r.failed) },
            { key: 'd', label: 'Date', align: 'right', render: (r) => dateTime(r.completedAt ?? r.startedAt) },
          ]}
        />
      </div>
    ),
  };
}

// ---------------------------------------------------------------- API & infra

export function apiDetail(payload: DashboardPayload, p: ChartPalette, key: string): Detail {
  const a = payload.sections.apis?.data;
  const u = a?.upstreams.find((x) => x.key === key);
  const probes = a?.probes.filter((x) => (key === 'v1' ? x.platform === 'v1' : key === 'data' ? x.platform === 'data' : key === 'adm' ? x.platform === 'adm' : x.platform === 'v2')) ?? [];
  const errs = a?.topErrors.filter((x) => x.service === key) ?? [];
  const slow = a?.slow.filter((x) => x.service === key) ?? [];
  const color = key === 'v1' ? p.v1 : key === 'data' ? p.third : p.v2;
  return {
    title: u?.label ?? key,
    explain: (
      <p>
        Trafic réel lu dans les logs d'accès de l'ingress nginx (Loki), hors sondes Front Door et UptimeRobot, sur les 5 dernières minutes. p95 = 95 % des requêtes
        répondent plus vite que cette valeur. Les routes sont normalisées (identifiants remplacés par :id / :uuid).
      </p>
    ),
    body: (
      <div className="space-y-4">
        {u && (
          <div className="grid grid-cols-2 gap-x-6">
            <Row label="Requêtes / min" value={dec(u.rpm)} />
            <Row label="Erreurs 5xx (5 min)" value={num(u.errors5xx5m)} hint={pct(u.errorRatePct, 2)} />
            <Row label="Erreurs 4xx (5 min)" value={num(u.errors4xx5m)} />
            <Row label="p50" value={ms(u.p50Ms)} />
            <Row label="p95" value={ms(u.p95Ms)} />
            <Row label="p99" value={ms(u.p99Ms)} />
          </div>
        )}
        {probes.map((pr) => (
          <Row key={pr.key} label={`Sonde ${pr.label}`} value={<OkMark ok={pr.ok} label={pr.status ? `HTTP ${pr.status}` : 'KO'} />} hint={ms(pr.latencyMs)} />
        ))}
        <div>
          <h4 className="mb-1 text-xs font-medium uppercase text-slate-400">Temps de réponse p95 (24 h)</h4>
          <TimeChart defs={[{ metric: `api.${key}.p95_ms`, label: 'p95', color }]} format={ms} height={140} />
        </div>
        <div>
          <h4 className="mb-1 text-xs font-medium uppercase text-slate-400">Requêtes / min (24 h)</h4>
          <TimeChart defs={[{ metric: `api.${key}.rpm`, label: 'req/min', color }]} height={120} area />
        </div>
        <div>
          <h4 className="mb-1 text-xs font-medium uppercase text-slate-400">p95 et taux d'erreur par jour</h4>
          <DailyChart defs={[{ metric: `api.${key}.p95_ms_day`, label: 'p95 du jour', color }]} format={ms} height={120} />
        </div>
        <h4 className="text-xs font-medium uppercase text-slate-400">Routes en erreur 5xx (dernière heure)</h4>
        <DataTable
          rows={errs}
          empty="Aucune erreur 5xx sur la dernière heure"
          columns={[
            { key: 'r', label: 'Route', render: (r) => <code className="text-xs">{r.method} {r.route}</code> },
            { key: 's', label: 'Code', align: 'right', render: (r) => r.status },
            { key: 'n', label: 'Nb', align: 'right', render: (r) => num(r.count) },
          ]}
        />
        <h4 className="text-xs font-medium uppercase text-slate-400">Routes lentes &gt; 3 s (dernière heure)</h4>
        <DataTable
          rows={slow}
          empty="Aucune requête de plus de 3 s"
          columns={[
            { key: 'r', label: 'Route', render: (r) => <code className="text-xs">{r.method} {r.route}</code> },
            { key: 'n', label: 'Nb', align: 'right', render: (r) => num(r.count) },
          ]}
        />
      </div>
    ),
  };
}

export function infraDetail(payload: DashboardPayload, p: ChartPalette): Detail {
  const d = payload.sections.infra?.data;
  return {
    title: 'Infrastructure AKS',
    explain: (
      <p>
        Prometheus de la stack de supervision (node-exporter, cAdvisor, kube-state-metrics). Le pool applicatif « prodfr » héberge les applications ; les pools
        prodfrdb / prodfrpg / prodfrlog hébergent MariaDB, PostgreSQL et la supervision.
      </p>
    ),
    body: d && (
      <div className="space-y-4">
        <div>
          <h4 className="mb-1 text-xs font-medium uppercase text-slate-400">CPU et mémoire du pool applicatif (24 h)</h4>
          <Legend
            defs={[
              { metric: 'infra.app_cpu_pct', label: 'CPU', color: p.v2 },
              { metric: 'infra.app_mem_pct', label: 'Mémoire', color: p.third },
            ]}
          />
          <TimeChart
            defs={[
              { metric: 'infra.app_cpu_pct', label: 'CPU', color: p.v2 },
              { metric: 'infra.app_mem_pct', label: 'Mémoire', color: p.third },
            ]}
            format={(n) => `${dec(n, 0)} %`}
            domainMax={100}
            height={150}
          />
        </div>
        <DataTable
          rows={d.nodes}
          columns={[
            { key: 'n', label: 'Nœud', render: (r) => <span className="text-xs">{r.name}</span> },
            { key: 'p', label: 'Pool', render: (r) => r.pool },
            { key: 'r', label: 'État', render: (r) => <OkMark ok={r.ready} label={r.ready ? 'Ready' : 'NotReady'} /> },
            { key: 'c', label: 'CPU', align: 'right', render: (r) => pct(r.cpuPct, 0) },
            { key: 'm', label: 'Mém.', align: 'right', render: (r) => `${pct(r.memPct, 0)} / ${dec(r.memGb)} Go` },
            { key: 'd', label: 'Disque', align: 'right', render: (r) => pct(r.diskPct, 0) },
            { key: 'pods', label: 'Pods', align: 'right', render: (r) => `${num(r.pods)} / ${num(r.podCapacity)}` },
          ]}
        />
        <DataTable
          rows={d.workloads}
          columns={[
            { key: 'l', label: 'Charge', render: (r) => r.label },
            { key: 'p', label: 'Pods', align: 'right', render: (r) => num(r.pods) },
            { key: 'c', label: 'CPU', align: 'right', render: (r) => `${dec(r.cpuCores, 2)} cœur` },
            { key: 'm', label: 'Mémoire', align: 'right', render: (r) => bytes(r.memBytes) },
            { key: 'r', label: 'Redém. 24 h', align: 'right', render: (r) => num(r.restarts24h) },
          ]}
        />
        {(d.problems.pods.length > 0 || d.problems.unavailable.length > 0 || d.problems.restarts1h.length > 0) && (
          <div className="space-y-1">
            <h4 className="text-xs font-medium uppercase text-slate-400">Problèmes</h4>
            {d.problems.pods.map((x) => (
              <Row key={x.pod} label={x.pod} value={x.reason} />
            ))}
            {d.problems.unavailable.map((x) => (
              <Row key={x.name} label={`${x.kind} ${x.name}`} value={`${x.missing} manquante(s)`} />
            ))}
            {d.problems.restarts1h.map((x) => (
              <Row key={x.pod + x.container} label={`${x.pod} (${x.container})`} value={`${x.count} redémarrage(s) / 1 h`} />
            ))}
          </div>
        )}
      </div>
    ),
  };
}

export function cronDetail(payload: DashboardPayload): Detail {
  const d = payload.sections.infra?.data;
  return {
    title: 'Tâches planifiées (CronJobs)',
    explain: <p>kube-state-metrics : dernier succès de chaque CronJob du cluster, comparé à sa planification (en retard = aucun succès depuis 2 intervalles + 15 min), et jobs en échec créés dans les dernières 24 h.</p>,
    body: d && (
      <DataTable
        rows={d.cronjobs}
        columns={[
          { key: 'n', label: 'Tâche', render: (r) => <span className="text-xs">{r.short}</span> },
          { key: 'p', label: 'Plateforme', render: (r) => r.platform },
          { key: 's', label: 'Planif.', render: (r) => <code className="text-xs">{r.schedule}</code> },
          { key: 'l', label: 'Dernier succès', align: 'right', render: (r) => (r.lastSuccessAgoSec === null ? 'jamais' : `il y a ${duration(r.lastSuccessAgoSec)}`) },
          { key: 'f', label: 'Échecs 24 h', align: 'right', render: (r) => num(r.failedLast24h) },
          { key: 'st', label: 'État', render: (r) => (r.suspended ? 'suspendue' : <OkMark ok={!r.stale && r.failedLast24h === 0} label={r.stale ? 'en retard' : r.failedLast24h ? 'échec' : 'OK'} />) },
        ]}
      />
    ),
  };
}

export function databasesDetail(payload: DashboardPayload, p: ChartPalette): Detail {
  const d = payload.sections.databases?.data;
  const q = payload.sections.queues?.data;
  return {
    title: 'Bases de données et files',
    explain: <p>MariaDB (V1 + ADM) et PostgreSQL (V2) : connexions, débit (delta entre deux collectes), requête la plus longue, réplication. Files LavinMQ du bus Messenger V2 : messages en attente.</p>,
    body: (
      <div className="space-y-4">
        {d?.mariadb && (
          <div>
            <h4 className="mb-1 text-xs font-medium uppercase text-slate-400">MariaDB</h4>
            {d.mariadb.error ? (
              <p className="text-sm text-red-600">{d.mariadb.error}</p>
            ) : (
              <div className="grid grid-cols-2 gap-x-6">
                <Row label="Connexions" value={`${num(d.mariadb.threadsConnected)} / ${num(d.mariadb.maxConnections)}`} />
                <Row label="Requêtes en cours" value={num(d.mariadb.threadsRunning)} />
                <Row label="Requêtes / s" value={dec(d.mariadb.qps)} />
                <Row label="Requêtes lentes / min" value={dec(d.mariadb.slowPerMin, 2)} />
                <Row label="Volume" value={`${dec(d.mariadb.sizeGb)} Go`} hint={`${num(d.mariadb.databases)} bases`} />
                <Row label="Démarré" value={`il y a ${duration(d.mariadb.uptimeSec)}`} />
              </div>
            )}
          </div>
        )}
        {d?.postgres && (
          <div>
            <h4 className="mb-1 text-xs font-medium uppercase text-slate-400">PostgreSQL V2</h4>
            {d.postgres.error ? (
              <p className="text-sm text-red-600">{d.postgres.error}</p>
            ) : (
              <div className="grid grid-cols-2 gap-x-6">
                <Row label="Connexions" value={`${num(d.postgres.connections?.total)} / ${num(d.postgres.maxConnections)}`} hint={`${num(d.postgres.connections?.active)} actives`} />
                <Row label="Idle in transaction" value={num(d.postgres.connections?.idleInTransaction)} />
                <Row label="Transactions / s" value={dec(d.postgres.tps)} />
                <Row label="Cache" value={pct(d.postgres.cacheHitPct, 2)} />
                <Row label="Requête la plus longue" value={duration(d.postgres.longestQuerySec)} />
                <Row label="Réplication" value={d.postgres.replicas ? `${num(d.postgres.replicas)} réplique(s), ${dec(d.postgres.replicationLagSec, 2)} s` : '–'} />
                <Row label="Volume" value={`${dec(d.postgres.sizeGb)} Go`} hint={`${num(d.postgres.databases)} bases`} />
                <Row label="Deadlocks (5 min)" value={num(d.postgres.deadlocks)} />
              </div>
            )}
            <div className="mt-2">
              <TimeChart defs={[{ metric: 'db.pg.tps', label: 'Transactions / s', color: p.v2 }]} height={110} />
            </div>
          </div>
        )}
        {q && (
          <div>
            <h4 className="mb-1 text-xs font-medium uppercase text-slate-400">Files LavinMQ</h4>
            <DataTable
              rows={[...q.transports, { name: 'failed', messages: q.failed, ready: q.failed, unacked: 0, consumed: true }]}
              columns={[
                { key: 'n', label: 'File', render: (r) => r.name },
                { key: 'm', label: 'En attente', align: 'right', render: (r) => num(r.messages) },
                { key: 'c', label: 'Worker', render: (r) => (r.consumed ? 'oui' : <span className="text-orange-600 dark:text-orange-400">aucun worker déployé</span>) },
              ]}
            />
          </div>
        )}
      </div>
    ),
  };
}

export function opsDetail(payload: DashboardPayload): Detail {
  const d = payload.sections.platform?.data;
  const ops = d?.ops;
  return {
    title: 'Exploitation ADM',
    explain: <p>Sauvegardes (table backup, dernières 26 h), cron des compteurs historiques ef:metrics:get, année fiscale courante (Référentiels › Années fiscales), dernière passe du rattrapage automatique des utilisateurs.</p>,
    body: ops && (
      <div className="grid grid-cols-2 gap-x-6">
        <Row label="Sauvegardes V1 (26 h)" value={num(ops.backups.v1)} hint={`/ ~${num(d?.fleet.v1Production)}`} />
        <Row label="Sauvegardes V2 (26 h)" value={num(ops.backups.v2)} />
        <Row label="ef:metrics:get V1" value={dateTime(ops.metricsLastRun.v1)} />
        <Row label="ef:metrics:get V2" value={dateTime(ops.metricsLastRun.v2)} />
        <Row label="Année fiscale courante" value={ops.schoolYear?.code ?? <span className="text-red-600">aucune</span>} />
        <Row label="Rattrapage auto" value={ops.autoCatchUp ? `${ops.autoCatchUp.status} · ${dateTime(ops.autoCatchUp.started_at)}` : '–'} hint={ops.autoCatchUp ? `+${num(ops.autoCatchUp.added_records)}` : undefined} />
      </div>
    ),
  };
}

export function collectDetail(payload: DashboardPayload, capturedAt: string): Detail {
  const sections = Object.entries(payload.sections);
  return {
    title: 'Collecte du tableau',
    explain: (
      <p>
        Commande <code>ef:dashboard:collect</code> toutes les 5 minutes (lecture seule sur toutes les sources). Chaque source est isolée : une source en échec garde
        ses dernières données. Le balayage des bases tenants V2 tourne toutes les 15 minutes. La dernière photo de chaque journée est conservée comme « fin de journée ».
      </p>
    ),
    body: (
      <div className="space-y-2">
        <Row label="Photo" value={dateTime(capturedAt)} hint={`${num(payload.durationMs)} ms`} />
        <DataTable
          rows={sections}
          columns={[
            { key: 'k', label: 'Source', render: ([k]) => k },
            { key: 's', label: 'État', render: ([, s]) => (s?.status === 'ok' ? (s.reused ? 'réutilisée' : 'OK') : s?.status) },
            { key: 'c', label: 'Collectée', align: 'right', render: ([, s]) => ago(s?.collectedAt) },
            { key: 'd', label: 'Durée', align: 'right', render: ([, s]) => ms(s?.durationMs) },
            { key: 'e', label: 'Erreur', render: ([, s]) => <span className="text-xs text-red-600">{s?.error}</span> },
          ]}
        />
      </div>
    ),
  };
}

